import { useEffect, useState } from 'react';
import {
  PlusIcon,
  DocumentArrowDownIcon,
  PencilSquareIcon,
  XMarkIcon,
  Cog8ToothIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { format } from 'date-fns';
import { addDays } from 'date-fns/esm';
import InvoiceItemTable from '../../../components/invoice/InvoiceItemTable';
import Select from '../../../components/select/Select';
import Button from '../../../components/button/Button';
import InvoicePaperView from '../../../components/invoice/InvoicePaperView';
import { Company } from '../../../types/Company';
import {
  DirectionDoc,
  DocumentType,
  Payment,
  InvoiceBaseInputs,
  DocumentTypeTranslated,
  DocumentStatus,
  RoundOptionsTranslated,
  OptionalFieldType,
  DocumentOptionalFieldLink,
  VatRateTranslated,
  VatRate,
} from '../../../types/Invoice';
import {
  calculateTotal,
  calculateTotalWithVat,
  getGroupedVatSummaryData,
  roundNumber,
} from '../../../utils/functions/calc.function';
import SelectAutocomplete from '../../../components/select/SelectAutocomplete';
import { CompanyBankAccount } from '../../../types/CompanyBankAccount';
import { NumericalSeries } from '../../../types/NumericalSeries';
import { Modal } from '../../../components/modal/modal';
import { InvoiceSubscriberForm } from '../../../forms/InvoiceSubscriber/InvoiceSubscriberModalForm';
import { useCurrentCompany } from '../../../hooks/useCompany';
import { dateSubmitFormat, getCurrentLangFormat } from '../../../types/core/date.types';
import {
  areArraysEqual,
  convertArrayToObjArray,
  convertEnumToArray,
  formatPrice,
} from '../../../utils/functions/data.functions';
import http, { authHeaders } from '../../../api/axios';
import { apiUrl } from '../../../api/apiUrl';
import { InvoiceSidePanel } from './InvoiceSidePanel';
import { Currency } from '../../../types/Currency';
import { RowData, initialData } from '../../../components/invoice/InvoiceItemTable.types';
import { VatCode, VatCodeTextNote } from '../../../types/VatCode';
import { defaultLang, useTranslation } from '../../../context/translationContext';
import { EntityType } from '../../../types/Files';
import { LangTranslated } from '../../../types/core/language';
import { toast } from 'react-toastify';
import { handleApiErrors } from '../../../api/apiErrorHandler';
import { CodeBookItem } from '../../../types/CodeBookItem';
import { Values } from '../../../forms/InvoiceSubscriber/InvoiceSubscriberModalForm.types';
import * as Yup from 'yup';
import { PrintData } from '../../PrintInvoice/PrintInvoicePage';
import { Datepicker } from '../../../components/datepicker/Datepicker';
import { useLoading } from '../../../context/loadingContext';
import { InvoiceCopy } from '../../../components/invoice/InvoiceCopy';
import { useNavigate } from 'react-router-dom';
import { paths } from '../../RouterContainer';
import ListButton, { ListButtonEntityType } from '../../../components/listButton/ListButton';

interface Props {
  data: InvoiceBaseInputs;
  onSubmit?: (data: InvoiceBaseInputs, hideInfoSuccess?: boolean) => any;
  documentNumber?: string;
  setDocumentNumber?: any;
  variableSymbol?: string | null;
  setVariableSymbol?: React.Dispatch<React.SetStateAction<string | undefined>>;
  numericalSeries?: NumericalSeries[];
  dateOfTaxableTransaction: Date;
  setDateOfTaxableTransaction?: any;
  companyBankAccounts?: CompanyBankAccount[];
  selectedNumericalSeries?: NumericalSeries | null;
  setSelectedNumericalSeries?: any;
  currenciesToTaxDate?: Currency[];
  currencyRatesDate?: Date;
  setCurrencyRatesDate?: any;
  isPrint?: boolean;
  onExport?: () => void;
  mutate?: () => void;
}

/**
 * Invoice form component
 */

export const InvoiceForm = ({
  data,
  onSubmit,
  documentNumber,
  setDocumentNumber,
  variableSymbol,
  setVariableSymbol,
  numericalSeries,
  dateOfTaxableTransaction,
  setDateOfTaxableTransaction,
  companyBankAccounts,
  selectedNumericalSeries,
  setSelectedNumericalSeries,
  currenciesToTaxDate,
  currencyRatesDate,
  setCurrencyRatesDate,
  isPrint,
  onExport,
  mutate,
}: Props) => {
  const navigate = useNavigate();
  const [printData, setPrintData] = useState<PrintData>();
  const { company } = useCurrentCompany();
  const { t, changeLang, langSetting, lang } = useTranslation();
  const { startLoading, stopLoading } = useLoading();
  // round numbers to 2 places
  const rounding = 2;
  // round for document´s total due
  const [round, setRound] = useState<string>(data.rounding === 0 ? 'false' : 'true');
  const [filteredNS, setFilteredNS] = useState<NumericalSeries[] | undefined>(numericalSeries);
  const [vatCodes, setVatCodes] = useState<VatCode[] | undefined>();
  const [codeBookItems, setCodeBookItems] = useState<CodeBookItem[] | undefined>();
  const [orderNumber, setOrderNumber] = useState<string | undefined>(data.orderNumber);
  const [note, setNote] = useState<string | undefined>(data.note);
  const [itemHeadline, setItemHeadline] = useState<string | undefined>(data.itemHeadline);
  const [dateOfIssue, setDateOfIssue] = useState<Date>(
    data.issued ? new Date(data.issued) : new Date(),
  );
  const [dateUntilDue, setDateUntilDue] = useState<Date>(
    data.dueDate ? new Date(data.dueDate) : addDays(new Date(), 14),
  );
  const [paymentDate, setPaymentDate] = useState<Date>(
    data.paymentDate ? new Date(data.paymentDate) : new Date(),
  );
  const [invoiceItems, setInvoiceItems] = useState<any[]>(data.items ?? [initialData]);
  const [documentType, setDocumentType] = useState<DocumentType>(data.type ?? DocumentType.INVOICE);
  const [isEditDocumentType, setIsEditDocumentType] = useState<boolean>(false);
  const [vatSummaryGroupedData, setVatSummaryGroupedData] = useState<any>();
  const [invoiceTotal, setInvoiceTotal] = useState<number>(data.total ?? 0);
  const [invoiceTotalWithVAT, setInvoiceTotalWithVAT] = useState<number>(data.totalWithVat ?? 0);
  const [documentLang, setDocumentLang] = useState<string>();
  const [documentStatus, setDocumentStatus] = useState<DocumentStatus>(
    data.documentStatus as DocumentStatus,
  );
  const [vatCodeTextNotes, setVatCodeTextNotes] = useState<VatCodeTextNote[] | null>(null);
  const [vatCodeText, setVatCodeText] = useState<string[] | null>(
    data.vatCodeSummaryTextNote ? data.vatCodeSummaryTextNote.split(';') : null,
  );
  const [uniqueVatCodesIds, setUniqueVatCodesIds] = useState<number[]>([]);

  const [isDeliveryAddress, setIsDeliveryAddress] = useState<boolean>(
    data.isSubscriberDeliveryDifferent ?? false,
  );
  // const [errors, setErrors] = useState<string[]>([]);
  const [showIbanSwift, setShowIbanSwift] = useState<boolean>(
    data.iban || data.swift ? true : false,
  );
  useEffect(() => {
    if (data.rounding === 0) return setRound('false');
    return setRound('true');
  }, [data]);

  const entityType = EntityType.ACCOUNTING_DOCUMENT;

  useEffect(() => {
    if (!isPrint) return;
    const params = new URLSearchParams(window.location.search);
    const fullName = params.get('fullName');
    const phoneNumber = params.get('phoneNumber');
    const codeBookItemsUseCode = params.get('codeBookItemsUseCode');
    const maxDecimalNumUnitPrice = params.get('maxDecimalNumUnitPrice');
    setPrintData({
      fullName: fullName,
      phoneNumber: phoneNumber,
      codeBookItemsUseCode: codeBookItemsUseCode === 'true' ? true : false,
      maxDecimalNumUnitPrice: maxDecimalNumUnitPrice ? Number(maxDecimalNumUnitPrice) : 2,
    });
  }, []);

  /**
   * If company changes while on document detail redirect to document overview
   */
  useEffect(() => {
    if ((company && company?.id === data?.companyId) || isPrint) return;
    navigate(`/${paths.INVOICES}`);
  }, [company]);

  /**
   * Language setting
   */
  useEffect(() => {
    if (!data || documentLang) return;
    handleChangeInvoiceLang(
      data.language ? data.language : langSetting?.defaultLanguage ?? defaultLang,
    );
  }, [data]);

  // Pre-fill subscriber data
  useEffect(() => {
    if (!data || !data.subscriberName) return;
    setSubscriberData(data);
  }, [data]);

  const handleChangeInvoiceLang = (lang: string) => {
    setDocumentLang(lang);
    changeLang(lang, entityType);
  };

  const [selectedBankAccount, setSelectedBankAccount] = useState<CompanyBankAccount>();
  const [payment, setPayment] = useState<Payment>({
    bankAccountNumber: data.bankAccountNumber
      ? data.bankAccountNumber
      : companyBankAccounts?.length === 1
      ? companyBankAccounts[0].number
      : undefined,
    currency: data.currency
      ? currenciesToTaxDate?.find((x: Currency) => x.code === data.currency)
      : currenciesToTaxDate && currenciesToTaxDate[0],
    specificSymbol: data.specificSymbol ? data.specificSymbol : undefined,
    variableSymbol: data.variableSymbol
      ? data.variableSymbol
      : variableSymbol
      ? variableSymbol
      : '',
    swift: data.swift ?? undefined,
    iban: data.iban ?? undefined,
  });
  const [selectedSubscriber, setSelectedSubscriber] = useState<Company | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [openIssueModal, setOpenIssueModal] = useState<boolean>(false);
  const [openCurrencyModal, setOpenCurrencyModal] = useState<boolean>(false);
  const [openCopyModal, setOpenCopyModal] = useState<boolean>(false);
  const [itemHints, setItemHints] = useState<any[]>([]);
  const [subscriberHints, setSubscriberHints] = useState<Company[]>([]);
  const [defaultCurrency, setDefaultCurrency] = useState<Currency | null>(null);
  // to store company logo
  const placeholderImg = `/img/placeholder-image.png`;
  const [imgSrc, setImgSrc] = useState<string>(placeholderImg);

  /**
   * Loads company logo
   */
  const fetchCompanyLogo = async () => {
    if (!company && !data.companyId) return;
    await http
      .apiGetFiles({
        url: `${apiUrl.COMPANY_LOGO_ITEM}?companyId=${company?.id ?? data.companyId}`,
        config: {
          headers: {
            ...authHeaders(),
            'Content-Type': 'application/json',
          },
          method: 'GET',
          responseType: 'blob',
        },
      })
      .then(async (response) => {
        if (response?.statusText === 'No Content') return setImgSrc(placeholderImg);
        const fileUrl: string = URL.createObjectURL(response?.data);
        setImgSrc(fileUrl);
      });
  };

  /**
   * Loads default currency - also needed for print view
   */
  const fetchDefaultCurrency = async () => {
    if (defaultCurrency) return;
    const response = await http.apiGet({
      url: `${apiUrl.CURRENCY_GET_DEFAULT_CURRENCY}`,
    });
    if (response?.data) {
      if (!data.currency) setPayment({ ...payment, currency: response.data });
      setDefaultCurrency(response?.data);
    }
  };

  /**
   * Loads vat codes for select2
   */
  const fetchVatCodes = async () => {
    if (!dateOfTaxableTransaction) return;
    const response = await http.apiGet({
      url: `${apiUrl.VAT_CODE_GET_LIST_FILTERED}?direction=${
        DirectionDoc.OUTPUT_DOC
      }&validToDate=${format(new Date(dateOfTaxableTransaction), dateSubmitFormat)}`,
    });
    if (response?.data) setVatCodes(response?.data);
  };

  /**
   * Loads code book items for select2
   */
  const fetchCodeBookItems = async () => {
    if (!company) return;
    const response = await http.apiGet({
      url: `${apiUrl.CODE_BOOK_ITEM_LIST_WITH_PRICES}?parentId=${company.id}&validToDate=${format(
        dateOfTaxableTransaction,
        dateSubmitFormat,
      )}`,
    });
    if (response?.data) {
      setCodeBookItems(response?.data);
    }
  };

  useEffect(() => {
    fetchDefaultCurrency();
    fetchCompanyLogo();
  }, []);

  useEffect(() => {
    if (!dateOfTaxableTransaction) return;
    fetchVatCodes();
    fetchCodeBookItems();
  }, [dateOfTaxableTransaction]);

  // Update currency data if date changes
  useEffect(() => {
    const activeCurrency = payment.currency?.code;
    setPayment({
      ...payment,
      currency: currenciesToTaxDate?.find((x: Currency) => x.code === activeCurrency),
    });
  }, [currenciesToTaxDate]);

  /**
   * Loads hints for previous used items in invoice
   */
  const fetchSubscriberHintsDataData = async (query: string) => {
    if (!company || query.trim().length === 0) return;

    const response = await http.apiGet({
      url: `${apiUrl.ACCOUNTING_DOCUMENT_SUBSCRIBER_HINT}?companyId=${company.id}&documentType=${documentType}&search=${query}`,
    });
    let hints: any[] = [];
    if (response?.data)
      response?.data.map(
        (item: any, index: number) => (hints = [...hints, { ...item, tmpId: index }]),
      );
    setSubscriberHints(hints);
  };

  /**
   * Set search query for invoice subscriber hints
   * @param query
   */
  const handleChangeSubscriberNameQuery = (query: string) => {
    if (query === '') return;
    fetchSubscriberHintsDataData(query);
  };

  const sortAndFilterDuplicateItems = (items: any) => {
    const sortedItems = items.sort((a: any, b: any) => {
      if (a.isFromCodeBookItemList && !b.isFromCodeBookItemList) {
        return -1;
      } else if (!a.isFromCodeBookItemList && b.isFromCodeBookItemList) {
        return 1;
      } else {
        // Both items have the same `isFromCodeBookItemList` value
        return a.name.localeCompare(b.name); // Sort by `name` asc
      }
    });

    const filteredItems: any[] = [];
    sortedItems.forEach((item: any) => {
      if (filteredItems.length === 0) {
        filteredItems.push(item);
      } else {
        const foundItem = filteredItems.find(
          (x) =>
            x.code === item.code &&
            x.name === item.name &&
            x.vatCode === item.vatCode &&
            x.price === item.price &&
            x.currency === item.currency &&
            x.unit === item.unit,
        );
        if (!foundItem) filteredItems.push(item);
      }
    });
    return filteredItems;
  };

  /**
   * Loads hints for previous used items in invoice
   */
  const fetchItemHintsDataData = async (query: string) => {
    if (!company) return;
    const response = await http.apiGet({
      url: `${apiUrl.ACCOUNTING_DOCUMENT_ITEM_HINTS_MERGED}?companyId=${company.id}&documentType=${documentType}&search=${query}`,
    });
    let hints: any[] = [];
    if (response?.data)
      response?.data.map(
        (item: any, index: number) => (hints = [...hints, { ...item, tmpId: index }]),
      );
    setItemHints(sortAndFilterDuplicateItems(hints));
  };

  /**
   * Set search query for invoice items hints
   * @param query
   */
  const handleChangeItemNameQuery = (query: string) => {
    if (query === '') return;
    fetchItemHintsDataData(query);
  };

  /**
   * Select subscriber from modal dialog
   */
  const handleSelectSubscriberByModal = (data: Values) => {
    setSubscriberData(data);
    setOpen(false);
  };

  /**
   * Select subscriber, note, itemHeadline by autofill from input
   */
  const handleSelectSubscriber = (data: InvoiceBaseInputs) => {
    if (data.numberOfDaysDue) setDateUntilDue(addDays(new Date(), data.numberOfDaysDue));
    if (data.note) setNote(data.note);
    if (data.itemHeadline) setItemHeadline(data.itemHeadline);
    setSubscriberData(data);
  };

  /**
   * After numerical series selection generate and set var. symbol
   */
  useEffect(() => {
    if (variableSymbol) setPayment({ ...payment, variableSymbol: variableSymbol });
  }, [variableSymbol]);

  const setSubscriberData = (data: any) => {
    if (!data) return;
    setSelectedSubscriber({
      id: data.subscriberCompanyId,
      name: data.subscriberName ? data.subscriberName : data.name,
      supplierVatId: data.subscriberVatId,
      supplierCompanyId: data.subscriberCompanyId,
      supplierAddressLine1: data.subscriberAddressLine1,
      supplierAddressLine2: data.subscriberAddressLine2,
      supplierAddressCity: data.subscriberAddressCity,
      supplierAddressPostCode: data.subscriberAddressPostCode,
      supplierAddressCountry: data.subscriberAddressCountry,
      supplierDeliveryName: data.subscriberDeliveryName,
      supplierDeliveryAddressLine1: data.subscriberDeliveryAddressLine1,
      supplierDeliveryAddressLine2: data.subscriberDeliveryAddressLine2,
      supplierDeliveryAddressCity: data.subscriberDeliveryAddressCity,
      supplierDeliveryAddressPostCode: data.subscriberDeliveryAddressPostCode,
      supplierDeliveryAddressCountry: data.subscriberDeliveryAddressCountry,
    });
  };
  /**
   * Prepares data for submit
   */
  const prepDataForSubmit = () => {
    const prepData: InvoiceBaseInputs = {
      id: data.id ? data.id : undefined,
      companyId: company?.id,
      conversionRate: 1,
      currency: payment.currency?.code,
      currencyRatesDate: currencyRatesDate,
      dateOfTaxableTransaction: format(dateOfTaxableTransaction, dateSubmitFormat),
      paymentDate: format(paymentDate, dateSubmitFormat),
      orderNumber: orderNumber,
      rounding: round === 'true' ? rounding : 0,
      direction: data.direction ?? DirectionDoc.OUTPUT_DOC,
      documentNumber: documentNumber,
      documentStatus: documentStatus,
      issued: format(dateOfIssue, dateSubmitFormat),
      dueDate: format(dateUntilDue, dateSubmitFormat),
      items: invoiceItems.filter(
        (item: any) =>
          (item.name !== '' && item.quantity && item.price !== null) || item.price !== undefined,
      ),
      variableSymbol: payment?.variableSymbol,
      specificSymbol: payment?.specificSymbol,
      bankAccountNumber: payment?.bankAccountNumber,
      subscriberCompanyId: selectedSubscriber?.supplierCompanyId,
      subscriberName: selectedSubscriber?.name,
      subscriberVatId: selectedSubscriber?.supplierVatId,
      subscriberAddressCity: selectedSubscriber?.supplierAddressCity,
      subscriberAddressLine1: selectedSubscriber?.supplierAddressLine1,
      subscriberAddressLine2: selectedSubscriber?.supplierAddressLine2,
      subscriberAddressPostCode: selectedSubscriber?.supplierAddressPostCode,
      subscriberAddressCountry: selectedSubscriber?.supplierAddressCountry,
      subscriberDeliveryName: selectedSubscriber?.supplierDeliveryName,
      subscriberDeliveryAddressLine1: selectedSubscriber?.supplierDeliveryAddressLine1,
      subscriberDeliveryAddressLine2: selectedSubscriber?.supplierDeliveryAddressLine2,
      subscriberDeliveryAddressCity: selectedSubscriber?.supplierDeliveryAddressCity,
      subscriberDeliveryAddressPostCode: selectedSubscriber?.supplierDeliveryAddressPostCode,
      subscriberDeliveryAddressCountry: selectedSubscriber?.supplierDeliveryAddressCountry,
      isSubscriberDeliveryDifferent: isDeliveryAddress,
      supplierAddressCity: company?.supplierAddressCity,
      supplierAddressLine1: company?.supplierAddressLine1,
      supplierAddressLine2: company?.supplierAddressLine2,
      supplierAddressPostCode: company?.supplierAddressPostCode,
      supplierAddressCountry: company?.supplierAddressCountry,
      supplierCompanyId: company?.supplierCompanyId,
      supplierName: company?.name,
      supplierVatId: company?.supplierVatId,
      total: invoiceTotal,
      totalWithVat: invoiceTotalWithVAT,
      type: documentType,
      note: note,
      itemHeadline: itemHeadline,
      language: documentLang,
      iban: payment?.iban,
      swift: payment?.swift,
      vatCodeSummaryTextNote:
        vatCodeTextNotes && vatCodeTextNotes?.length > 0
          ? getTextsByLanguage(vatCodeTextNotes)?.join(';')
          : null,
    };
    return prepData;
  };

  /**
   * Prepares data for submit and submits (create / update)
   */
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!company || !onSubmit) return;
    onSubmit(prepDataForSubmit());
  };

  /**
   * Check if document is valid then opens modal
   */
  const handleOpenIssueModal = async () => {
    try {
      // check for errors
      await schema.validate(
        {
          ...prepDataForSubmit(),
        },
        // get all errors at once
        { abortEarly: false },
      );
      // document is valid --> open issue modal
      setOpenIssueModal(true);
    } catch (err) {
      setOpenIssueModal(false);
    }
  };

  /**
   * Function for getting date for issue document by document type
   */
  const getDateForIssueDocument = () => {
    if (!documentType) return;
    if (documentType === DocumentType.DEPOSIT_SLIP)
      return format(new Date(dateOfIssue), dateSubmitFormat);
    return format(new Date(dateOfTaxableTransaction), dateSubmitFormat);
  };

  /**
   * Issues document
   */
  const handleIssueDocument = async () => {
    if (!onSubmit) return;
    try {
      startLoading();
      const date = getDateForIssueDocument();
      // save changes on document
      await onSubmit(prepDataForSubmit(), true);
      const response = await http.apiPut({
        url: `${apiUrl.ACCOUNTING_DOCUMENT_ISSUE}?documentId=${data.id}&date=${date}&numericalSeries=${selectedNumericalSeries?.id}`,
      });
      if (response?.status === 200) {
        stopLoading();
        setDocumentStatus(response.data.documentStatus);
        setVariableSymbol && setVariableSymbol(response.data.variableSymbol);
        setDocumentNumber(response.data.documentNumber);
        toast.success('Success!');
        setOpenIssueModal(false);
      }
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
      setOpenIssueModal(false);
    }
  };

  useEffect(() => {
    if (!vatCodes) return;
    setInvoiceTotal(calculateTotal(invoiceItems));
    setInvoiceTotalWithVAT(calculateTotalWithVat(invoiceItems));
    // if (!invoiceItems) setInvoiceItems(data.items ? data.items : [initialData]);
    setVatSummaryGroupedData(getGroupedVatSummaryData(invoiceItems, vatCodes));
  }, [data, vatCodes]);

  useEffect(() => {
    if (!vatCodes) return;
    setInvoiceTotal(calculateTotal(invoiceItems));
    setInvoiceTotalWithVAT(calculateTotalWithVat(invoiceItems));
    setVatSummaryGroupedData(getGroupedVatSummaryData(invoiceItems, vatCodes));
  }, [invoiceItems, vatCodes]);

  /**
   * Fill input when account number from autocomplete is selected or create new, when none is found
   * new type string, existing is of type object
   */
  const handleChangeAccNumber = (val: string | CompanyBankAccount) => {
    if (typeof val === 'object') {
      // item selected -> fill bankAccountNumber and swift, IBAN if exists && showIbanSwift = true
      const swift = showIbanSwift ? val.swift : undefined;
      const iban = showIbanSwift ? val.iban : undefined;
      setSelectedBankAccount(val);
      setPayment({ ...payment, bankAccountNumber: val.number, swift: swift, iban: iban });
    } else {
      // new item
      setPayment({ ...payment, bankAccountNumber: val });
    }
  };

  useEffect(() => {
    if (!numericalSeries) return;
    const _filteredNs = numericalSeries?.filter(
      (ns: NumericalSeries) => ns.documentType === documentType,
    );
    if (data.documentNumber === '' || data.variableSymbol === '') {
      if (_filteredNs && _filteredNs.length === 1) {
        setSelectedNumericalSeries(numericalSeries[0]);
      }
    }
    setFilteredNS(_filteredNs);
  }, [documentType]);

  /**
   * Document type changed -> filter numerical series and change values of doc. number and VS
   */
  const hangleDocumentTypeChanged = (e: any) => {
    setDocumentType(e.value);
    setSelectedNumericalSeries(null);
    setIsEditDocumentType(false);
  };

  /**
   * Controls if IBAN and Swift fields should be shown
   * If false set both variables to undefined
   */
  const handleShowIbanSwift = (show: boolean) => {
    if (!show) setPayment({ ...payment, iban: undefined, swift: undefined });
    if (show)
      setPayment({
        ...payment,
        iban: selectedBankAccount?.iban,
        swift: selectedBankAccount?.swift,
      });
    setShowIbanSwift(show);
  };

  useEffect(() => {
    if (!dateOfIssue || dateOfTaxableTransaction) return;
    setDateOfTaxableTransaction(dateOfIssue);
  }, [dateOfIssue]);

  /**
   * Fetch vat codes text notes by id
   * @param id
   */
  const fetchVatCodeTextNote = async (id: number) => {
    const response = await http.apiGet({
      url: `${apiUrl.VAT_CODE_TEXT_NOTE_LIST}?parentId=${id}`,
    });
    if (response?.data) return response?.data;
  };

  useEffect(() => {
    if (invoiceItems.length === 0) return;
    const _uniqueVatCodesIds: number[] = [];
    invoiceItems.map((item: RowData) => {
      const vatCode = vatCodes?.find((vatCode: VatCode) => vatCode.code === item.vatCode);
      if (vatCode && !_uniqueVatCodesIds.includes(vatCode?.id))
        _uniqueVatCodesIds.push(vatCode?.id);
    });
    // Dont fetch notes if they are already fetched
    if (areArraysEqual(uniqueVatCodesIds, _uniqueVatCodesIds)) return;
    setUniqueVatCodesIds(_uniqueVatCodesIds);
    // Fetch notes for each unique vat code
    const getNotes = async () => {
      const notes = await Promise.all(
        _uniqueVatCodesIds.map((id: number) => fetchVatCodeTextNote(id)),
      );
      setVatCodeTextNotes(notes);
    };
    getNotes();
  }, [invoiceItems, documentLang]);

  useEffect(() => {
    if (!vatCodeTextNotes) return;
    const texts = getTextsByLanguage(vatCodeTextNotes);
    setVatCodeText(texts ?? []);
  }, [vatCodeTextNotes, documentLang]);

  /**
   * Returns VAT note texts by language
   * @param arr
   */
  const getTextsByLanguage = (arr: any[]) => {
    if (!arr || !arr.length) return null;
    const texts = [];
    for (let i = 0; i < arr.length; i++) {
      if (!arr[i]) return;
      for (let j = 0; j < arr[i].length; j++) {
        const item = arr[i][j];
        if (item.language.toLocaleLowerCase() === documentLang?.toLocaleLowerCase())
          texts.push(item.text);
      }
    }
    // Check for duplicates
    // Convert the array to a Set to remove duplicates
    const uniqueSet = new Set(texts);
    // Convert the Set back to an array
    const uniqueArray = Array.from(uniqueSet);
    return uniqueArray;
  };

  /**
   * Renders supplier box
   */
  const renderSupplier = () => {
    return (
      <div className="p-1">
        <div className="font-bold text-lg">{t('page.invoice.supplier', entityType)}</div>
        <div>
          <ul>
            <li>{company?.name ? company?.name : data.supplierName}</li>
            <li>
              {company?.supplierAddressLine1
                ? company?.supplierAddressLine1
                : data.supplierAddressLine1}
            </li>
            <li>
              {company?.supplierAddressLine2
                ? company?.supplierAddressLine2
                : data.supplierAddressLine2}
            </li>
            <li>
              {company?.supplierAddressPostCode
                ? company?.supplierAddressPostCode
                : data.supplierAddressPostCode}
              {(company?.supplierAddressPostCode || data.supplierAddressPostCode) && `, `}
              {company?.supplierAddressCity
                ? company?.supplierAddressCity
                : data.supplierAddressCity}
            </li>
            <li>
              {company?.supplierAddressCountry
                ? company?.supplierAddressCountry
                : data.supplierAddressCountry}
            </li>
            {imgSrc !== placeholderImg && <br />}
            <div className="flex justify-between w-full">
              {(company?.supplierCompanyId || data.supplierCompanyId) && (
                <li>
                  {t('page.invoice.company-id', entityType)}{' '}
                  {company?.supplierCompanyId ? company?.supplierCompanyId : data.supplierCompanyId}
                </li>
              )}
              {(company?.supplierVatId || data.supplierVatId) && (
                <li className="ml-3">
                  {t('page.invoice.vat-id', entityType)}{' '}
                  {company?.supplierVatId ? company?.supplierVatId : data.supplierVatId}
                </li>
              )}
            </div>
          </ul>
        </div>
      </div>
    );
  };

  /**
   * Renders subscriber box
   */
  const renderSubscriber = () => {
    return (
      <>
        <div className="font-bold text-lg">{t('page.invoice.subscriber', entityType)}</div>
        <div className="p-1">
          <div>
            {selectedSubscriber ? (
              <>
                <ul>
                  <li className="flex flex-row">
                    {selectedSubscriber?.name}
                    {!isPrint && (
                      <div className="absolute right-8">
                        <Button
                          className="px-2 mr-3"
                          onClick={() => setOpen(true)}
                          icon={<PencilSquareIcon className="w-5 h-5 text-white" />}
                        />
                        <Button
                          className="px-2"
                          color="red"
                          onClick={() => setSelectedSubscriber(null)}
                          icon={<XMarkIcon className="w-5 h-5 text-white" />}
                        />
                      </div>
                    )}
                  </li>
                  <li>{selectedSubscriber?.supplierAddressLine1}</li>
                  <li>{selectedSubscriber?.supplierAddressLine2}</li>
                  <li>
                    {selectedSubscriber?.supplierAddressPostCode}
                    {selectedSubscriber?.supplierAddressPostCode &&
                      selectedSubscriber?.supplierAddressCity &&
                      `, `}
                    {selectedSubscriber?.supplierAddressCity}
                  </li>
                  <li>{selectedSubscriber?.supplierAddressCountry}</li>
                  <div className="flex justify-between w-3/5 mt-2">
                    <li>
                      {t('page.invoice.company-id', entityType)}{' '}
                      {selectedSubscriber?.supplierCompanyId}
                    </li>
                    {selectedSubscriber?.supplierVatId && (
                      <li>
                        {t('page.invoice.vat-id', entityType)} {selectedSubscriber?.supplierVatId}
                      </li>
                    )}
                  </div>
                </ul>
                {isDeliveryAddress && (
                  <>
                    <ul className="mt-1">
                      <p className="underline mb-1">
                        {t('page.invoice.delivery-address', entityType)}
                      </p>
                      <li className="flex flex-row">{selectedSubscriber?.supplierDeliveryName}</li>
                      <li>{selectedSubscriber?.supplierDeliveryAddressLine1}</li>
                      <li>{selectedSubscriber?.supplierDeliveryAddressLine2}</li>
                      <li>
                        {selectedSubscriber?.supplierDeliveryAddressPostCode}
                        {selectedSubscriber?.supplierDeliveryAddressPostCode &&
                          selectedSubscriber?.supplierDeliveryAddressCity &&
                          `, `}
                        {selectedSubscriber?.supplierDeliveryAddressCity}
                      </li>
                      <li>{selectedSubscriber?.supplierDeliveryAddressCountry}</li>
                    </ul>
                  </>
                )}
              </>
            ) : (
              <>
                <p className="font-bold mb-2">{t('page.invoice.select-subscriber', entityType)}</p>
                <div>
                  <div className="flex flex-row items-center">
                    <SelectAutocomplete
                      data={subscriberHints ? subscriberHints : []}
                      labelKey="subscriberName"
                      valueKey="name"
                      placeholder={t('page.invoice.enter-subscriber-name', entityType)}
                      isReadOnly={isPrint}
                      onSelected={(e) => handleSelectSubscriber(e)}
                      onChange={() => null}
                      onQueryChange={async (e) => handleChangeSubscriberNameQuery(e)}
                      className={`p-1 w-3/4`}
                      forceSelect
                      resize={false}
                    />
                    <div className="inline-flex bg-blue-500 p-1 rounded-lg cursor-pointer text-white">
                      <PlusIcon
                        onClick={() => setOpen(true)}
                        className="w-5 h-5 bg-blue-500 text-white"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-row justify-between">
          <div
            className={`box-border w-full ${
              documentType !== DocumentType.DEPOSIT_SLIP && `w-1/2`
            } py-1`}
          >
            <div>{t('page.invoice.input-date-of-issue', entityType)}</div>
            <div className="w-4/5">
              <Datepicker
                value={new Date(dateOfIssue)}
                onChange={(e) => e && setDateOfIssue(e)}
                placeholder={t('page.invoice.input-date-of-issue', entityType)}
                readOnly={isPrint}
                format={getCurrentLangFormat(documentLang)}
              />
            </div>
          </div>
          {documentType !== DocumentType.DEPOSIT_SLIP &&
            documentType !== DocumentType.TAX_RECEIPT_FOR_PAYMENT_RECIEVED && (
              <div className="box-border py-1 w-1/2">
                <span>{t('page.invoice.input-date-taxable-supply', entityType)}</span>
                <div className="w-4/5">
                  <Datepicker
                    value={
                      dateOfTaxableTransaction
                        ? new Date(dateOfTaxableTransaction)
                        : data && data?.dateOfTaxableTransaction
                        ? new Date(data?.dateOfTaxableTransaction)
                        : new Date()
                    }
                    onChange={(e) => e && setDateOfTaxableTransaction(e)}
                    placeholder={t('page.invoice.input-date-of-issue', entityType)}
                    readOnly={isPrint}
                    format={getCurrentLangFormat(documentLang)}
                  />
                </div>
              </div>
            )}
          {documentType === DocumentType.TAX_RECEIPT_FOR_PAYMENT_RECIEVED && (
            <div className="box-border py-2 px-2 w-1/2">
              <span>{t('page.invoice.input-date-payment', entityType)}</span>
              <div className="w-4/5">
                <Datepicker
                  value={new Date(paymentDate)}
                  onChange={(e) => e && setPaymentDate(e)}
                  placeholder={t('page.invoice.input-date-of-payment', entityType)}
                  readOnly={isPrint}
                  format={getCurrentLangFormat(documentLang)}
                />
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  /**
   * Renders payment box
   */
  const renderPaymentInfo = () => {
    if (documentType === DocumentType.TAX_RECEIPT_FOR_PAYMENT_RECIEVED) return;
    return (
      <div className="row-span-2 col-span-2">
        <div className="font-bold text-lg">{t('page.invoice.payment-info', entityType)}</div>
        <div className="border-gray-300 border border-1 px-2 py-1">
          <div className="flex justify-between items-center">
            <span>{t('page.invoice.input-payment-method', entityType)} </span>
            <span className="w-1/2 text-right float-right">
              {isPrint ? (
                <span>Bank transfer</span>
              ) : (
                <Select
                  data={[
                    { id: 0, name: 'Bank transfer' },
                    { id: 1, name: 'Cash' },
                    { id: 3, name: 'Bitcoin' },
                    { id: 4, name: 'Check' },
                  ]}
                  labelKey="name"
                  className={`float-right !m-0 !py-0.5`}
                  isReadOnly={isPrint}
                  onChange={(e) => console.log(e, 'aaaa')}
                />
              )}
            </span>
          </div>
          {/* acc number */}
          <div className="flex justify-between mt-1">
            <span>{t('page.invoice.input-account-number', entityType)}</span>
            <div>
              <SelectAutocomplete
                data={companyBankAccounts ? companyBankAccounts : []}
                value={payment.bankAccountNumber}
                labelKey="name"
                valueKey="number"
                isReadOnly={isPrint}
                onSelected={(val) => handleChangeAccNumber(val)}
                onChange={(val) => handleChangeAccNumber(val)}
                displayValue={'number'}
                resize={false}
              />
            </div>
          </div>
          {!isPrint && (
            <div className="w-full flex justify-between pt-1">
              <label htmlFor={'showIbanSwift'} className="mr-3">
                {t('page.invoice.show-iban-swift', entityType)}
              </label>
              <input
                name="showIbanSwift"
                type={'checkbox'}
                checked={showIbanSwift}
                onChange={(e) => handleShowIbanSwift(e.target.checked)}
              />
            </div>
          )}
          {showIbanSwift && (
            <div>
              <div className="pt-1 flex">
                <span className="mr-3">{t('page.invoice.input-iban', entityType)}</span>
                <input
                  value={payment.iban ?? ''}
                  onChange={(e) => setPayment({ ...payment, iban: e.target.value })}
                  className="border border-gray-300 rounded-md appearance-none block px-1 placeholder-gray-500 text-gray-900 focus:outline-none w-full"
                  type="text"
                  placeholder={t('page.invoice.input-iban', entityType)}
                  readOnly={isPrint}
                />
              </div>
              <div className="pt-1 flex">
                <span className="mr-3">{t('page.invoice.input-swift', entityType)}</span>
                <input
                  value={payment.swift ?? ''}
                  onChange={(e) => setPayment({ ...payment, swift: e.target.value })}
                  className="border border-gray-300 w-full rounded-md appearance-none block px-1 placeholder-gray-500 text-gray-900 focus:outline-none"
                  type="text"
                  placeholder={t('page.invoice.input-swift', entityType)}
                  readOnly={isPrint}
                />
              </div>
            </div>
          )}
          <div className="flex justify-between mt-1">
            <span>{t('page.invoice.input-variable-symbol', entityType)}:</span>
            <input
              value={payment?.variableSymbol}
              onChange={(e) => setPayment({ ...payment, variableSymbol: e.target.value })}
              className={`border border-gray-300 w-36 rounded-md appearance-none block px-3 placeholder-gray-500 text-gray-900 focus:outline-none`}
              type="text"
              placeholder={t('page.invoice.input-variable-symbol', entityType)}
              readOnly={isPrint}
              disabled
            />
          </div>
          {(!isPrint || payment?.specificSymbol) && (
            <div className="flex justify-between mt-1">
              <span>{t('page.invoice.input-specific-symbol', entityType)}:</span>
              <input
                value={payment?.specificSymbol}
                onChange={(e) => setPayment({ ...payment, specificSymbol: e.target.value })}
                className={`border border-gray-300 w-36 rounded-md appearance-none block px-3 placeholder-gray-500 text-gray-900 focus:outline-none}`}
                type="text"
                placeholder={t('page.invoice.input-specific-symbol', entityType)}
                readOnly={isPrint}
              />
            </div>
          )}
          <div className="flex justify-between mt-1">
            <span>{t('page.invoice.input-date-until-due', entityType)}</span>
            <div className="w-36">
              <Datepicker
                value={new Date(dateUntilDue)}
                onChange={(e: Date | null) => e && setDateUntilDue(e)}
                placeholder={t('page.invoice-menu.date')}
                readOnly={isPrint}
                format={getCurrentLangFormat(documentLang)}
              />
            </div>
          </div>

          <div className="flex justify-between items-center w-full mt-1">
            <span>{t('page.invoice.total-due', entityType)}</span>
            <span className="text-lg ml-auto text-right font-bold">
              {round === 'true'
                ? formatPrice(roundNumber(invoiceTotalWithVAT, 0))
                : formatPrice(roundNumber(invoiceTotalWithVAT, rounding))}{' '}
              {payment.currency?.code ?? data.currency}
            </span>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Renders note
   */
  const renderNote = () => {
    return (
      <div className="mr-3">
        <div className="flex flex-col">
          <span className="font-bold">{t('page.invoice.note', entityType)}</span>
          <div>
            <textarea
              name="note"
              placeholder={t('page.invoice.enter-note', entityType)}
              defaultValue={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              className="mt-1 w-full px-1 mr-5"
              style={{ resize: 'none' }}
            />
          </div>
        </div>
      </div>
    );
  };

  /**
   * Renders price summary
   */
  const renderPriceSummary = () => {
    return (
      <div className="p-2 w-5/12 border border-gray-300">
        <div className="flex flex-row justify-between border-b border-gray-300">
          <div className="inline-flex items-center">
            <span>{t('page.invoice.round', entityType)}</span>
            {!isPrint && (
              <Select
                data={convertArrayToObjArray(['true', 'false'], RoundOptionsTranslated, t)}
                labelKey="name"
                valueKey="value"
                className="ml-1 py-1"
                minWidth={100}
                value={round}
                showCheckIcon={false}
                onChange={(e) => setRound(e.value)}
              />
            )}
          </div>
          <span className="text-right mt-2">
            {round === 'true'
              ? (
                  roundNumber(invoiceTotalWithVAT, 0) - roundNumber(invoiceTotalWithVAT, rounding)
                ).toLocaleString()
              : 0}{' '}
            {payment.currency?.code ?? data.currency}
          </span>
        </div>
        <div className="flex flex-row justify-between py-1">
          <span>{t('page.invoice.summary-total-due', entityType)}</span>
          <span className="text-right">
            {round === 'true'
              ? formatPrice(roundNumber(invoiceTotalWithVAT, 0))
              : formatPrice(roundNumber(invoiceTotalWithVAT, rounding))}{' '}
            {payment.currency?.code ?? data.currency}
          </span>
        </div>
      </div>
    );
  };
  /**
   * Renders VAT summary
   */
  const renderVatSummary = () => {
    if (documentType === DocumentType.DEPOSIT_SLIP) return;
    return (
      <div className="w-[70%]">
        <div className="flex justify-between items-center w-2/3">
          <span className="pr-2 font-bold">
            {t('page.invoice.vat-summary-headline', entityType)}
          </span>
          {payment.currency?.code && payment.currency?.code !== defaultCurrency?.code && (
            <div className="mt-1">
              <span className="mr-2">{t('page.invoice.currency-rate', entityType)}</span>
              <span>{payment.currency?.rate.toLocaleString('cs-CZ')}</span>
            </div>
          )}
        </div>
        <div className="flex items-center">
          <div>
            {vatCodeText?.map((text: string, index: number) => (
              <span key={index} className="block">
                {text}
              </span>
            ))}
          </div>
          {!isPrint && vatCodeText && vatCodeText.length > 1 && (
            <>
              <ExclamationTriangleIcon
                className="w-6 h-6 ml-10 text-yellow-500"
                title="VAT error"
              />
              <span>{t('page.invoice.vat-summary.error', entityType)}</span>
            </>
          )}
        </div>
        <table className="table-auto mt-3 w-full">
          <thead className="border-b border-gray-300">
            <tr>
              <th className="text-left">{t('page.invoice.vat-summary.col-rate', entityType)}</th>
              <th className="text-left">
                {t('page.invoice.vat-summary.col-vat-rate', entityType)}
              </th>
              <th className="text-right">
                {t('page.invoice.vat-summary.col-vat-base', entityType)}
              </th>
              <th className="text-right">{t('page.invoice.vat-summary.col-vat', entityType)}</th>
              <th className="text-right">{t('page.invoice.vat-summary.col-total', entityType)}</th>
            </tr>
          </thead>
          <tbody>
            {vatSummaryGroupedData &&
              vatSummaryGroupedData.map((i: any, index: number) => {
                return (
                  <tr key={index}>
                    <td>{t(VatRateTranslated[i?.rate as VatRate], entityType)}</td>
                    <td>{i.ratio} %</td>
                    <td className="text-right">
                      {formatPrice(
                        roundNumber(
                          payment.currency?.rate && defaultCurrency?.rate
                            ? (i.vatBase * payment.currency?.rate) / defaultCurrency?.rate
                            : i.vatBase,
                          2,
                        ),
                      )}{' '}
                      {defaultCurrency?.code}
                    </td>
                    <td className="text-right">
                      {formatPrice(
                        roundNumber(
                          payment.currency?.rate && defaultCurrency?.rate
                            ? (i.vat * payment.currency?.rate) / defaultCurrency.rate
                            : i.vat,
                          2,
                        ),
                      )}{' '}
                      {defaultCurrency?.code}
                    </td>
                    <td className="text-right">
                      {formatPrice(
                        roundNumber(
                          payment.currency?.rate && defaultCurrency?.rate
                            ? (i.total * payment.currency?.rate) / defaultCurrency.rate
                            : i.total,
                          2,
                        ),
                      )}{' '}
                      {defaultCurrency?.code}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    );
  };

  /**
   * Renders invoice items
   */
  const renderInvoiceItems = () => {
    return (
      <div>
        <div>
          {!isPrint && (
            <textarea
              name="item-headline"
              placeholder={t('page.invoice.enter-item-headline', entityType)}
              defaultValue={itemHeadline}
              onChange={(e) => setItemHeadline(e.target.value)}
              rows={1}
              className="w-full px-2 mr-5"
              style={{ resize: 'none' }}
            />
          )}
          {isPrint && itemHeadline && <>{itemHeadline}</>}
        </div>
        <div>
          {currenciesToTaxDate && payment.currency && (
            <InvoiceItemTable
              items={invoiceItems}
              onItemsChange={setInvoiceItems}
              rounding={rounding}
              itemHints={itemHints}
              setItemHints={setItemHints}
              onChangeNameQuery={handleChangeItemNameQuery}
              isReadOnly={isPrint}
              vatCodes={vatCodes}
              codeBookItems={codeBookItems}
              codeBookItemsUseCode={
                company?.codeBookItemsUseCode
                  ? company?.codeBookItemsUseCode
                  : printData?.codeBookItemsUseCode
                  ? printData?.codeBookItemsUseCode
                  : false
              }
              invoiceCurrency={payment.currency}
              maxDecimalNumUnitPrice={printData?.maxDecimalNumUnitPrice}
              isPrint={isPrint}
            />
          )}
        </div>
      </div>
    );
  };

  /**
   * Renders footer
   */
  const renderFooter = () => {
    return (
      <div className={`absolute ${isPrint ? `bottom-0` : `bottom-5`}`}>
        {/* User info */}
        {isPrint && printData && (
          <span className={`h-[20px] mt-5 block`}>
            {`${
              printData?.fullName
                ? t('page.invoice.issued-by', entityType) + ': ' + printData?.fullName
                : ``
            } ${
              printData?.phoneNumber
                ? ', ' + t('page.invoice.tel-num', entityType) + ': ' + printData.phoneNumber
                : ``
            } `}
          </span>
        )}
        {data && data.optionalFields && (
          <span className={`h-[40px]`}>
            {data.optionalFields.map((x: DocumentOptionalFieldLink) => {
              if (x.optionalField.language !== documentLang?.toLocaleLowerCase()) return;
              if (
                x.optionalField.fieldType === OptionalFieldType.Footer &&
                x.optionalField.isActive
              )
                return x.optionalField.value;
            })}
          </span>
        )}
      </div>
    );
  };

  /**
   * Renders logo
   */
  const renderLogo = () => {
    if (imgSrc !== placeholderImg)
      return (
        <div className="max-h-[120px] w-min-max">
          <img src={imgSrc} alt="Company logo" className="max-w-[200px] max-h-[120px]" />
        </div>
      );
  };

  /**
   * Renders document info
   */
  const renderDocumentInfo = () => {
    return (
      <div className="min-w-[210px] p-1">
        {!isPrint && (
          <span className="absolute -top-10 -right-5">
            <ListButton
              params={documentStatus ?? (data.documentStatus as DocumentStatus)}
              entityType={ListButtonEntityType.DOCUMENT_STATUS}
            />
          </span>
        )}
        <div className="font-bold">
          <div className="inline-flex items-center text-lg">
            {/* document type */}
            {!isEditDocumentType && t(DocumentTypeTranslated[documentType], entityType)}
            {!isPrint && !isEditDocumentType && (
              <PencilSquareIcon
                className="w-5 h-5 ml-1 cursor-pointer"
                onClick={() => setIsEditDocumentType(!isEditDocumentType)}
              />
            )}
            {/* document type - edit dropdown */}
            {isEditDocumentType && (
              <div className="mr-5 min-w-1/2">
                <Select
                  data={convertEnumToArray(DocumentType, DocumentTypeTranslated, t)}
                  labelKey="name"
                  value={{
                    name: t(DocumentTypeTranslated[documentType]),
                    value: documentType,
                  }}
                  onChange={hangleDocumentTypeChanged}
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-between mt-1">
          <div className="mr-1">{t('page.invoice.input-document-number', entityType)}</div>
          {isPrint ? (
            documentNumber
          ) : (
            <div>
              <input
                className={`rounded-md appearance-none block px-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none`}
                type="text"
                value={documentNumber}
                placeholder={t('page.invoice.enter-document-number', entityType)}
                onChange={(e) => setDocumentNumber(e.target.value)}
                readOnly={isPrint}
                disabled
              />
            </div>
          )}
        </div>
        <div className="flex justify-between mt-1">
          {(!isPrint || (isPrint && orderNumber)) && (
            <div className="mr-1">{t('page.invoice.input-order-number', entityType)}</div>
          )}
          {isPrint ? (
            orderNumber
          ) : (
            <input
              className="rounded-md appearance-none block px-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:z-10"
              type="text"
              placeholder={t('page.invoice.enter-order-number', entityType)}
              value={orderNumber ?? ''}
              onChange={(e) => {
                setOrderNumber(e.target.value);
              }}
              readOnly={isPrint}
            />
          )}
        </div>
        <div className="flex justify-between mt-1">
          <div>{t('page.invoice.input-variable-symbol', entityType)}:</div>
          <div>{payment.variableSymbol}</div>
        </div>
      </div>
    );
  };

  // field validation
  // const errorClass = `border border-red-500`;

  // Validation messages
  /* const documentNumberReq = `validations.document-number-req`;
  const selectedNumSeriesReq = `validations.selected-num-series-req`;
  const variableSymbolReq = `validations.variable-symbol-req`; */

  const schema = Yup.object().shape({
    /* documentNumber: Yup.string().required(documentNumberReq),
    // not required in edit
    selectedNumericalSeriesId: Yup.number().when('id', {
      is: (id: string) => !id,
      then: Yup.number().required(selectedNumSeriesReq),
    }),
    variableSymbol: Yup.string().required(variableSymbolReq), */
  });

  /* useEffect(() => {
    const _errors: string[] = [];
    let cleanErr: string[] = [];
    if (documentNumber && documentNumber.length > 0) cleanErr = [...cleanErr, documentNumberReq];
    if (selectedNumericalSeries) cleanErr = [...cleanErr, selectedNumSeriesReq];
    if (payment && payment?.variableSymbol && payment?.variableSymbol.length > 0)
      cleanErr = [...cleanErr, variableSymbolReq];
    setErrors(_errors.filter((x: string) => !cleanErr.includes(x)));
  }, [documentNumber, selectedNumericalSeries, payment.variableSymbol]); */

  const dividerStyle = `my-2 border-t-1 border-gray-300`;
  return (
    <>
      <form onSubmit={handleSubmit}>
        <div>
          {!isPrint && (
            <div className="flex flex-row items-center">
              <div className="mr-5">
                <span className="mr-5">{t('page.invoice-menu.currency')}</span>
                {payment.currency && (
                  <>
                    <div className="flex items-center">
                      <span>{payment.currency?.name + ' - ' + payment.currency?.code}</span>
                      <Cog8ToothIcon
                        className="ml-1 w-5 h-5 mr-1 cursor-pointer"
                        onClick={() => setOpenCurrencyModal(true)}
                      />
                    </div>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 absolute">{`${t(
                      'page.invoice-menu.currency-rates-date',
                    )} ${
                      currencyRatesDate &&
                      format(new Date(currencyRatesDate), getCurrentLangFormat(lang))
                    }`}</p>
                  </>
                )}
              </div>
              <div className="w-40">
                <>
                  <span>{t('page.invoice-menu.document-lang')}</span>
                  {langSetting?.supportedDocLanguages && documentLang && (
                    <Select
                      data={convertArrayToObjArray(
                        langSetting?.supportedDocLanguages,
                        LangTranslated,
                        t,
                      )}
                      labelKey="name"
                      valueKey="value"
                      value={documentLang}
                      onChange={(e) => handleChangeInvoiceLang(e.value.toLocaleLowerCase())}
                    />
                  )}
                </>
              </div>
              {data.id && documentStatus !== DocumentStatus.ISSUED && (
                <Button
                  title={t('page.invoice-menu.btn-issue-document')}
                  color="green"
                  outline
                  textColor="default"
                  className={`mt-5 mr-3`}
                  onClick={handleOpenIssueModal}
                />
              )}
              <Button
                title={t('page.invoice-menu.btn-save-invoice')}
                color="green"
                className={`mt-5 mr-3`}
                onClick={handleSubmit}
                showLoading
              />
              {data.id && (
                <Button
                  className="items-center mt-5 mr-3"
                  onClick={() => setOpenCopyModal(true)}
                  title={t('page.invoice-menu.btn-copy-document')}
                  icon={<DocumentDuplicateIcon className="w-5 h-5 mr-1 cursor-pointer" />}
                />
              )}
              {onExport && (
                <Button
                  className="items-center mt-5 mr-3"
                  onClick={onExport}
                  title={t('page.invoice-menu.btn-export-invoice')}
                  showLoading
                  icon={
                    <DocumentArrowDownIcon
                      title={t('page.invoice-menu.btn-export-invoice')}
                      className="w-5 h-5 mr-1 cursor-pointer"
                    />
                  }
                />
              )}
              <Button
                title={t('general.back')}
                color="gray"
                className={`mt-5 mr-3`}
                onClick={() => navigate(`/${paths.INVOICES}`)}
                showLoading
              />
            </div>
          )}
          <div className="flex flex-row justify-between overflow-x-hidden text-xs m-auto">
            <InvoicePaperView isPrint={isPrint}>
              <div className={`${!isPrint && `p-3 relative`} ${isPrint && `pl-5`} !h-full !w-full`}>
                {/* body */}
                <div className="relative">
                  {/* header */}
                  {data && data.optionalFields && (
                    <span>
                      {data.optionalFields.map((x: DocumentOptionalFieldLink) => {
                        if (x.optionalField.language !== documentLang?.toLocaleLowerCase()) return;
                        if (
                          x.optionalField.fieldType === OptionalFieldType.Header &&
                          x.optionalField.isActive
                        )
                          return x.optionalField.value;
                      })}
                    </span>
                  )}
                  <div className="flex justify-between items-center">
                    {/* supplier */}
                    {renderSupplier()}
                    {/* --- logo / supplier box--- */}
                    {renderLogo()}
                    {/* --- document type --- */}
                    {renderDocumentInfo()}
                  </div>
                  <hr className={dividerStyle} />
                  <div className="grid grid-rows-2 grid-flow-col gap-4">
                    <div className="row-span-2">{renderPaymentInfo()}</div>
                    {/* --- subscriber --- */}
                    <div className="row-span-2">{renderSubscriber()}</div>
                  </div>
                  {data && data.optionalFields && (
                    <span>
                      {data.optionalFields.map((x: DocumentOptionalFieldLink) => {
                        if (x.optionalField.language !== documentLang?.toLocaleLowerCase()) return;
                        if (
                          x.optionalField.fieldType === OptionalFieldType.SectionUnderSupplier &&
                          x.optionalField.isActive
                        )
                          return x.optionalField.value;
                      })}
                    </span>
                  )}
                  <hr className={dividerStyle} />
                  {/* Invoice items */}
                  {renderInvoiceItems()}
                  <div className="w-full mx-auto flex justify-between mt-2">
                    <div className="w-7/12">
                      {/* Note */}
                      {(!isPrint || (isPrint && note)) && renderNote()}
                    </div>
                    {/* Price summary */}
                    {renderPriceSummary()}
                  </div>
                  <hr className={dividerStyle} />
                  <div className="center w-full">
                    {/* VAT summary */}
                    {renderVatSummary()}
                  </div>
                </div>
                {/* footer */}
                {(data.optionalFields || printData) && renderFooter()}
              </div>
            </InvoicePaperView>
            {data.id && !isPrint && (
              <InvoiceSidePanel entityId={data.id} data={data} mutate={mutate} />
            )}
          </div>
        </div>
      </form>
      {/* Dialog wherther user wants to copy linked documents  */}
      {data.id && (
        <InvoiceCopy
          documentId={data.id}
          onClose={() => setOpenCopyModal(false)}
          open={openCopyModal}
        />
      )}
      {/* Issue modal */}
      <Modal
        open={openIssueModal}
        title={t('page.invoice.issue-document-headline')}
        onClose={() => setOpenIssueModal(false)}
      >
        {filteredNS && (
          <div className="mr-5">
            <span className="mr-5">{t('page.invoice-menu.numerical-series')}</span>
            <Select
              data={[{ id: null, name: t('general.select') }, ...filteredNS]}
              labelKey="name"
              value={selectedNumericalSeries}
              onChange={(e) => setSelectedNumericalSeries(e)}
            />
            <Button
              title={t('page.invoice-menu.btn-issue-document')}
              className="bg-orange-600 hover:bg-orange-900 mt-5 mr-3"
              onClick={handleIssueDocument}
              disabled={!selectedNumericalSeries}
            />
          </div>
        )}
      </Modal>
      {/* Add / edit subscriber */}
      <Modal
        open={open}
        title={t('page.invoice.add-subscriber-headline')}
        className={`min-w-[45%]`}
        onClose={() => setOpen(false)}
      >
        <InvoiceSubscriberForm
          initialData={selectedSubscriber}
          onSubmit={handleSelectSubscriberByModal}
          onClose={() => setOpen(false)}
          isDeliveryAddress={isDeliveryAddress}
          setIsDeliveryAddress={setIsDeliveryAddress}
          documentType={documentType}
        />
      </Modal>
      {/* Currency setting modal */}
      <Modal
        open={openCurrencyModal}
        title={t('page.invoice.set-currency-headline')}
        className={`min-w-[20%]`}
        onClose={() => setOpenCurrencyModal(false)}
      >
        <div className="p-5">
          <div className="mr-5 grid">
            <span className="mr-5 w-max">{t('page.invoice-menu.currency-rates-date')}</span>
            {currencyRatesDate && (
              <Datepicker
                onChange={(e) => setCurrencyRatesDate(e)}
                readOnly={isPrint}
                placeholder={t('page.invoice-menu.currency-rates-date')}
                value={new Date(currencyRatesDate)}
                className="relative w-full border my-1 cursor-default rounded-lg bg-white py-2 h-9 pl-3 pr-5 text-left shadow-md focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white"
              />
            )}
          </div>
          {currenciesToTaxDate && (
            <div className="mr-5 grid">
              <span className="mr-5 w-max">{t('page.invoice-menu.currency')}</span>
              <Select
                data={currenciesToTaxDate}
                displayPattern={'country-code'}
                value={currenciesToTaxDate.find((x: Currency) => x.code === payment.currency?.code)}
                onChange={(e) => {
                  setPayment({ ...payment, currency: e });
                }}
                helperText={`${t('page.invoice-menu.currency-rates-date')} ${
                  currencyRatesDate &&
                  format(new Date(currencyRatesDate), getCurrentLangFormat(lang))
                }`}
              />
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};
